export type Situation = "death_occurred" | "planning_ahead";
export type PlanningFor = "self" | "loved_one" | "both";

export type WhereType =
  | "hospital"
  | "private_residence"
  | "care_facility"
  | "medical_examiner"
  | "other";

export type RelationType =
  | "spouse"
  | "parent"
  | "adult_child"
  | "child"
  | "grandparent"
  | "power_of_attorney"
  | "other";

export interface PersonName {
  firstName: string;
  lastName: string;
}

export interface RelationAnswer {
  type: RelationType | null;
  otherText: string;
}

/** The submitter's own plan. Their name is the submitter's name. */
export interface SelfAnswers extends PersonName {
  dob: string;
  zip: string;
  hospice: boolean | null;
}

export interface LovedOneAnswers extends PersonName {
  dob: string;
  zip: string;
  /** Only meaningful when planning for both: the loved one shares the
   * submitter's ZIP, so it is not asked again. */
  livesInMyZip: boolean;
  hospice: boolean | null;
  relation: RelationAnswer;
}

export interface SubmitterAnswers extends PersonName {
  email: string;
  phone: string;
}

/** Everything the visitor has typed or chosen, as typed. Branches that
 * the current situation does not use are kept so a changed answer does
 * not lose them, and are ignored when the submission is built. */
export interface Answers {
  situation: Situation | null;
  decedent: PersonName;
  where: { type: WhereType | null; zip: string };
  relation: RelationAnswer;
  readyForCare: boolean | null;
  preplanned: boolean | null;
  preplanReference: string;
  planningFor: PlanningFor | null;
  self: SelfAnswers;
  lovedOne: LovedOneAnswers;
  submitter: SubmitterAnswers;
}

export function emptyAnswers(): Answers {
  return {
    situation: null,
    decedent: { firstName: "", lastName: "" },
    where: { type: null, zip: "" },
    relation: { type: null, otherText: "" },
    readyForCare: null,
    preplanned: null,
    preplanReference: "",
    planningFor: null,
    self: { firstName: "", lastName: "", dob: "", zip: "", hospice: null },
    lovedOne: {
      firstName: "",
      lastName: "",
      dob: "",
      zip: "",
      livesInMyZip: true,
      hospice: null,
      relation: { type: null, otherText: "" },
    },
    submitter: { firstName: "", lastName: "", email: "", phone: "" },
  };
}

/**
 * The slice of a submission's answers the landing screen reads (names, the
 * place type, readiness, who the plan is for), with every contact detail,
 * date of birth, ZIP and free-text field blanked. A stored result carries
 * this instead of the answers, so the screen can be rebuilt after a reload
 * without keeping what the visitor typed.
 */
export function landingAnswersOf(a: Answers): Answers {
  const blank = emptyAnswers();
  const relation = (value: RelationAnswer): RelationAnswer => ({
    type: value.type,
    otherText: "",
  });
  return {
    ...blank,
    situation: a.situation,
    decedent: { ...a.decedent },
    where: { type: a.where.type, zip: "" },
    relation: relation(a.relation),
    readyForCare: a.readyForCare,
    preplanned: a.preplanned,
    planningFor: a.planningFor,
    self: {
      ...blank.self,
      firstName: a.self.firstName,
      lastName: a.self.lastName,
      hospice: a.self.hospice,
    },
    lovedOne: {
      ...blank.lovedOne,
      firstName: a.lovedOne.firstName,
      lastName: a.lovedOne.lastName,
      livesInMyZip: a.lovedOne.livesInMyZip,
      hospice: a.lovedOne.hospice,
      relation: relation(a.lovedOne.relation),
    },
    submitter: {
      ...blank.submitter,
      firstName: a.submitter.firstName,
      lastName: a.submitter.lastName,
    },
  };
}
